import type { RecipeNutrition, RecipeNutritionFilters, StorefrontRecipe } from "../api/types";
import type { NutrientValues } from "../api/nutrition";

/** Navigation cache belongs to the application, not a page component instance. */
export type RecipeNutritionSort = NonNullable<RecipeNutritionFilters["nutritionSort"]>;
/** Все фильтры книги, включая 4 КБЖУ-диапазона и сортировку; версия каталога хранится отдельно. */
export interface RecipeBookFilters extends Omit<RecipeNutritionFilters, "catalogVersion"> { search?: string; category?: string; maxTime?: number; diet?: string }
export interface RecipeBookFacets { categories: Array<{ value: string; label: string; count: number }>; diets: Array<{ value: string; label: string; count: number }>; timeMax: number[] }
export const recipeBookCache: {
  recipes: StorefrontRecipe[]; nextOffset: number | null; hasMore: boolean; loaded: boolean; scrollY: number;
  filters: RecipeBookFilters; facets: RecipeBookFacets | null;
  /** Версия подборки КБЖУ первой страницы: догрузка идёт только по ней. */
  catalogVersion?: string;
  /** Рецепты, для которых КБЖУ неизвестно при активном КБЖУ-фильтре (отдельно от результатов). */
  nutritionUnknown: StorefrontRecipe[];
  nutritionExcluded: number;
} = {
  recipes: [], nextOffset: 0, hasMore: true, loaded: false, scrollY: 0, filters: {}, facets: null,
  catalogVersion: undefined, nutritionUnknown: [], nutritionExcluded: 0,
};

let buyerEpoch = 0;
export function recipeBookBuyerEpoch(): number { return buyerEpoch; }
export function resetRecipeBookForBuyer(): void {
  buyerEpoch++;
  recipeBookCache.recipes.splice(0);
  Object.assign(recipeBookCache,{nextOffset:0,hasMore:true,loaded:false,scrollY:0,filters:{},facets:null,
    catalogVersion:undefined,nutritionUnknown:[],nutritionExcluded:0});
}

export const NUTRITION_FILTER_KEYS = ["kcalMin", "kcalMax", "proteinMin", "proteinMax", "fatMin", "fatMax", "carbsMin", "carbsMax"] as const;
export function hasNutritionFilter(f: RecipeBookFilters): boolean {
  return NUTRITION_FILTER_KEYS.some((k) => f[k] !== undefined);
}

// ─── Представление КБЖУ рецепта (числа только с сервера; здесь лишь маскировка и русские пояснения) ───

const NUTRIENT_KEYS = ["kcal", "protein_g", "fat_g", "carbs_g"] as const;
const NULLS: NutrientValues = { kcal: null, protein_g: null, fat_g: null, carbs_g: null };

const REASON_RU: Record<string, string> = {
  serves_changed: "Рецепт изменился после расчёта: поменялось число порций. КБЖУ пересчитаем после проверки.",
  ingredients_changed: "Рецепт изменился после расчёта: поменялся состав. КБЖУ пересчитаем после проверки.",
  steps_changed: "Рецепт изменился после расчёта: поменялся способ приготовления. КБЖУ пересчитаем после проверки.",
  items_changed: "Рецепт изменился после расчёта: поменялись продукты. КБЖУ пересчитаем после проверки.",
  unavailable_item_unverified: "Часть продуктов рецепта сейчас недоступна, поэтому КБЖУ не удаётся проверить.",
  source_not_checked: "Состав рецепта пока не удалось проверить, поэтому КБЖУ не показываем.",
  no_dataset_entry: "КБЖУ для этого рецепта пока не рассчитано.",
  calculation_failed: "Не удалось рассчитать КБЖУ для этого рецепта.",
  consumption_unknown: "Пока неизвестно, сколько технологических ингредиентов попадёт в порцию. Оценку по заложенным продуктам сохраняем отдельно.",
};
const STATUS_FALLBACK_RU: Record<string, string> = {
  stale: "Рецепт изменился после расчёта КБЖУ. Пересчитаем после проверки.",
  unverifiable: "КБЖУ рецепта сейчас не удаётся проверить.",
  no_data: "КБЖУ для этого рецепта пока не рассчитано.",
  unavailable: "Не удалось рассчитать КБЖУ для этого рецепта.",
};
const ASSUMPTION_RU: Record<string, string> = {
  optional_additions_excluded: "Добавки «по желанию» не учтены.",
  low_proxy_reference: "Для части продуктов взяты справочные значения близких по составу продуктов.",
  whole_aromatic_removed: "Целые пряности и травы, которые вынимают из блюда, не учтены.",
  full_transfer_assumed: "Считаем, что ингредиенты целиком попадают в блюдо.",
  cooking_medium_unknown: "Неизвестно, сколько масла или жидкости для готовки остаётся в блюде, — такие показатели отмечены прочерком.",
  consumption_unknown: "Для части ингредиентов неизвестно, сколько их попадает в порцию.",
  finished_mass_unknown: "Масса готовой порции неизвестна, поэтому КБЖУ на 100 г блюда не считаем.",
};
export const NUTRITION_LABEL_FALLBACK = "Расчётная оценка на порцию исходного рецепта; не лабораторное измерение";

export interface RecipeNutritionView {
  status: "complete" | "partial" | "unavailable";
  values: NutrientValues;
  reason: string | null;
  assumptions: string[];
  label: string;
}

/** Неизвестное и непроверенное — null (прочерк), никогда не 0; коды сервера наружу не выходят. */
export function recipeNutritionView(n: RecipeNutrition | undefined | null): RecipeNutritionView | null {
  if (!n) return null;
  const label = n.labelRu?.trim() ? n.labelRu : NUTRITION_LABEL_FALLBACK;
  if (n.status !== "available" && n.status !== "partial") {
    return { status: "unavailable", values: { ...NULLS }, reason: (n.reason && REASON_RU[n.reason]) || STATUS_FALLBACK_RU[n.status] || STATUS_FALLBACK_RU.unavailable, assumptions: [], label };
  }
  const unknown = new Set(n.unknownNutrients ?? []);
  const values = { ...NULLS };
  for (const k of NUTRIENT_KEYS) {
    const v = n.perServing?.[k];
    values[k] = !unknown.has(k) && typeof v === "number" && Number.isFinite(v) ? v : null;
  }
  const partial = n.status === "partial" || NUTRIENT_KEYS.some((k) => values[k] === null);
  const assumptions = [...new Set((n.assumptions ?? []).map((a) => ASSUMPTION_RU[a]).filter((a): a is string => !!a))];
  return {
    status: partial ? "partial" : "complete", values, assumptions, label,
    reason: partial ? "Прочерк — показатель неизвестен, это не ноль." : null,
  };
}

/** Короткая подпись для карточки: только известная калорийность. */
export function recipeKcalLabel(n: RecipeNutrition | undefined | null): string | null {
  const v = recipeNutritionView(n);
  const kcal = v?.values.kcal;
  return kcal === null || kcal === undefined ? null : `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(kcal)} ккал на порцию`;
}


/** URL contains navigation parameters only, never recipe/profile payloads or buyer identifiers. */
export function recipeFiltersFromUrl(params: URLSearchParams): RecipeBookFilters {
  const f: RecipeBookFilters = {};
  for (const key of ["search", "category", "diet"] as const) {
    const value = params.get(key)?.trim();
    if (value && value.length <= 512) f[key] = value;
  }
  for (const key of [...NUTRITION_FILTER_KEYS, "maxTime"] as const) {
    const raw = params.get(key), value = Number(raw);
    if (raw && Number.isFinite(value) && value >= 0 && value <= 100000 && (key !== "maxTime" || value > 0)) f[key] = value;
  }
  for (const [min, max] of [["kcalMin","kcalMax"],["proteinMin","proteinMax"],["fatMin","fatMax"],["carbsMin","carbsMax"]] as const) {
    if (f[min] !== undefined && f[max] !== undefined && f[min]! > f[max]!) { delete f[min]; delete f[max]; }
  }
  const sort = params.get("nutritionSort");
  if (["kcal_asc","protein_desc","fat_asc","carbs_asc"].includes(sort ?? "")) f.nutritionSort = sort as RecipeNutritionSort;
  return f;
}
export function recipeFiltersKey(filters: RecipeBookFilters): string {
  return JSON.stringify(Object.entries(filters).filter(([,v])=>v!==undefined&&v!=="").sort(([a],[b])=>a.localeCompare(b)));
}

/** Родительский список рецептов для ссылки «← К рецептам»: только локальный `/recipes` с исходным query
 * (фильтры, страницы, порядок, прокрутка). Внешнее, protocol-relative, иной путь, `#`, управляющие символы
 * (в том числе закодированные %00–%1F/%7F),
 * битое %-кодирование или слишком длинная строка → прежнее поведение `/recipes`. */
export const RECIPE_LIST_HREF = "/recipes";
export function recipeParentHref(raw: unknown): string {
  if (typeof raw !== "string" || raw.length > 4096) return RECIPE_LIST_HREF;
  if (!/^\/recipes(\?[^#\\\s\u0000-\u001f\u007f]*)?$/.test(raw)) return RECIPE_LIST_HREF;
  try {
    if (/[\u0000-\u001f\u007f]/.test(decodeURIComponent(raw))) return RECIPE_LIST_HREF;
    const url = new URL(raw, "http://recipes.invalid");
    if (url.origin !== "http://recipes.invalid" || url.pathname !== RECIPE_LIST_HREF || url.hash) return RECIPE_LIST_HREF;
  } catch { return RECIPE_LIST_HREF; }
  return raw;
}
